import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import useFetch from "hooks/useFetch";
import CustomButton from "components/Button";
import { CardContent, Card, CardActionArea, IconButton, Typography } from "@mui/material";
import CustomInput from "components/Input";
import { getToken } from "utils/getToken";
import CloseButtonIcon from "assets/icons/Close";
import useIsTablet from "hooks/useIsTablet";
import { useState } from "react";
import { toast } from "react-toastify";
import Search from "assets/icons/Search";
import Preloader from "components/GlobalPreloader/Preloader";

interface IDomain {
  name: string;
  listedDomain: string;
  createdAt: string;
  nsFirst: string;
  nsSecond: string;
  status: string;
  _id: string;
  pwa?: string;
  type: string;
}

interface IModalCheckDomain {
  open: boolean;
  setIsModalOpen: (value: boolean) => void;
  setDomainsList: (newDomain: IDomain) => void;
}

interface IDomain {
  domainName: string;
  price: number;
}

export default function ModalDomainPurchase({ open, setIsModalOpen, setDomainsList }: IModalCheckDomain) {
  const token = getToken();
  const { t } = useTranslation();
  const isTablet = useIsTablet();
  const [inputValue, setInputValue] = useState<string>("");
  const [inputError, setInputError] = useState<string>("");
  const [selectedCardIndex, setSelectedCardIndex] = useState<number | null>(null);
  const [selectedDomainName, setSelectedDomainName] = useState<string>("");
  const [domains, setDomains] = useState<IDomain[]>([]);
  const [alreadySearched, setAlreadySearched] = useState<boolean>(false);

  const { loading: loadingDomains, triggerFetch: searchDomains } = useFetch({
    url: "domain/search",
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: {
      keyword: inputValue,
    },
  });

  const { loading: loadingBuyDomain, triggerFetch: buyDomain } = useFetch({
    url: "domain/purchase",
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: {
      domainName: selectedDomainName,
    },
  });

  const isDomainsRequestLoading = loadingDomains || loadingBuyDomain;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.target;
    setInputValue(value);
  };

  const handleModalClose = () => {
    setDomains([]);
    setSelectedDomainName("");
    setSelectedCardIndex(null);
    setInputError("");
    setInputValue("");
    setAlreadySearched(false);
    setIsModalOpen(false);
  };

  const handleSearch = async () => {
    if (!inputValue) {
      setInputError("thisfieldisrequired");
      return;
    }

    setInputError("");
    setAlreadySearched(true);

    try {
      const domainsData = await searchDomains();

      if (domainsData.success) {
        setDomains(domainsData.data.domains);
      } else {
        toast.error(t("unsuccessfulDomainsSearch"));
      }
    } catch (error) {
      toast.error(t("unsuccessfulDomainsSearch"));
    }
  };

  const handleBuyDomain = async () => {
    if (!selectedDomainName) {
      toast.error(t("unselectedDomainName"));
      return;
    }

    try {
      const purchasedDomain = await buyDomain();

      if (purchasedDomain.success) {
        setDomainsList(purchasedDomain.data);
        handleModalClose();

        toast.success(t("domainPurchasedSuccessfully"));
      } else {
        toast.error(t("domainPurchaseFailed"));
      }
    } catch (error) {
      toast.error(t("domainPurchaseFailed"));
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="modal-purchase-domain-title"
      aria-describedby="modal-purchase-domain-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: "8px",
          padding: "32px 24px 24px 24px",
          bgcolor: "#FFFFFF",
          boxShadow: "0 0 4px 0 rgba(165, 163, 174, 0.3);",
          width: "90%",
          maxWidth: "780px",
          height: "auto",
          maxHeight: "90dvh",
          overflowY: "auto",
        }}
      >
        <IconButton
          sx={{ position: "absolute", top: 16, right: 16, width: 32, height: 32 }}
          aria-label="modal close button"
          onClick={handleModalClose}
        >
          <CloseButtonIcon />
        </IconButton>
        <Typography
          id="modal-modal-title"
          variant="h2"
          sx={{
            marginBottom: "14px",
            fontSize: "24px",
            fontWeight: 400,
            color: "#2E263D",
          }}
        >
          {t("domainsPurchaseModalTitle")}
        </Typography>
        <Typography
          sx={{
            marginBottom: "40px",
            fontSize: "14px",
            lineHeight: "1.35",
            fontWeight: 400,
            color: "#2E263D",
          }}
        >
          {t("domainsPurchaseModalDescription")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "6px", marginBottom: "24px" }}>
          <CustomInput
            required
            fullWidth
            id="domainSearch"
            value={inputValue}
            placeholder=""
            name="domainSearch"
            height="40px"
            autoFocus={isTablet ? false : true}
            customBgColor="#F6F7FB"
            customFontSize="14px"
            helperText={t(inputError)}
            error={inputError ? true : false}
            onChange={handleInputChange}
          />

          <IconButton
            sx={{ width: "40px", height: "40px", border: "1px solid #C9D0D8", borderRadius: "8px" }}
            aria-label="search-icon"
            onClick={handleSearch}
          >
            <Search />
          </IconButton>
        </Box>

        {alreadySearched && !domains.length && !isDomainsRequestLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "120px",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                lineHeight: "1.35",
                fontWeight: 400,
                color: "#2E263D",
              }}
            >
              {t("emptyData")}
            </Typography>
          </Box>
        )}

        <Box sx={{ flex: "1 1 auto", overflowY: "auto" }}>
          {loadingDomains ? (
            <Preloader customStyles={{ border: "none", width: "100%", height: "100%" }} />
          ) : (
            <Box
              sx={{
                width: "100%",
                marginBottom: "24px",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(min(200px, 100%), 1fr))",
                gap: 2,
              }}
            >
              {domains.map((domain, index) => (
                <Card key={`${domain.domainName}_${index}`} sx={{ boxShadow: "none" }}>
                  <CardActionArea
                    onClick={() => {
                      setSelectedCardIndex(index);
                      setSelectedDomainName(domain.domainName);
                    }}
                    data-active={selectedCardIndex === index ? "" : undefined}
                    sx={{
                      height: "100%",
                      border: "1px solid #C9D0D8",
                      "&[data-active]": {
                        backgroundColor: "#fff4ed",
                        border: "1px solid #f4701b",
                        "&:hover": {
                          backgroundColor: "action.selectedHover",
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ height: "100%" }}>
                      <Typography component="p" sx={{ fontSize: "16px", fontWeight: 600, color: "#2e263d" }}>
                        {domain.domainName}
                      </Typography>
                      <Typography
                        component="p"
                        sx={{
                          fontSize: "14px",
                          fontWeight: 400,
                          color: "#6d6777",
                          lineHeight: 1.43,
                        }}
                      >
                        {`${t("price")}: ${domain.price} `}{" "}
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "14px",
                            fontWeight: 400,
                            color: "#16B364",
                          }}
                        >
                          $
                        </Typography>
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: "8px",
            flexDirection: isTablet ? "column-reverse" : "row",
            alignItems: "center",
          }}
        >
          <CustomButton
            width="100%"
            height={40}
            textButton={t("cancel")}
            customColor="#6D6777"
            customBorderColor="#C9D0D8"
            customHoverColor="transparent"
            customBorderRadius="8px"
            customFontSize="14px"
            customTextTransform="none"
            onClick={handleModalClose}
          />
          <CustomButton
            width="100%"
            textButton={t("buydomain")}
            height={40}
            customBgColor="#F4701B"
            customHoverColor="#F4701B"
            customBorderRadius="8px"
            customFontSize="14px"
            customTextTransform="none"
            onClick={handleBuyDomain}
            loading={loadingBuyDomain}
            disabled={domains.length === 0 || loadingBuyDomain}
          />
        </Box>
      </Box>
    </Modal>
  );
}
